import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/components/ui/use-toast';
import { parseDoseCombination, calculateTierConcordance } from '../dose-utils';
import { callRailwayAPI } from './useRailwayAPI';
import type { ClinicianResponse, OutOfScopeDetail, RailwayResponse, SubmitPhase } from './types';

// Single source of truth for the assessment submit path shared by both arms
// (pregnancy + preconception). The seven historical divergences between the
// two ~750-line forms all lived in this flow (auto-save reconciliation, the
// F-01 retry handling, and the status='complete' bookkeeping); centralising it
// here removes the drift. Field layout and validation stay in each form because
// the two arms have legitimately different fields (e.g. gestational_week).
//
// Behaviour mirrors the audited pregnancy implementation exactly:
//   • rows are born 'pending_retry' (DB default) and earn 'complete' only after
//     concordance is saved — this is the single place that flips to 'complete';
//   • any failure of the Railway call / api_response / concordance insert flags
//     the case pending_retry with retry_attempts=1 and an error label, and the
//     flag-write itself is error-checked with a fallback toast so a case is
//     never silently lost (F-01);
//   • except a refusal at the device's API perimeter (HTTP 422, detail.action
//     'SHOW_OUT_OF_SCOPE'), which is an outcome: recorded as 'out_of_scope',
//     terminal, never retried (Clinical Owner decision 2026-09-28).

export interface ArmSubmitConfig<TClinical> {
  /** Result-route segment + concordance tier logic are arm-agnostic; arm drives the redirect. */
  arm: 'pregnancy' | 'preconception';
  tables: {
    cases: string;
    clinicianResponses: string;
    apiResponses: string;
    concordanceResults: string;
  };
  railwayUrl: string;
  /** Wrapper key the backend expects: 'patient_data' (pregnancy) or 'patient' (preconception). */
  payloadKey?: string;
  /** Maps arm-specific ClinicalData to the *_cases insert columns. Must NOT set status (DB default). */
  buildCaseInsert: (data: TClinical) => Record<string, unknown>;
  /** Maps arm-specific ClinicalData to the Railway request body. */
  buildRailwayPayload: (data: TClinical) => unknown;
  /** Extra columns on the success api_responses row, e.g. { strategy_used } for preconception. */
  buildApiResponseExtra?: (railwayData: RailwayResponse) => Record<string, unknown>;
}

/**
 * Records the device's refusal of a case (HTTP 422, SHOW_OUT_OF_SCOPE) as a terminal
 * 'out_of_scope' status. The api_responses row keeps what the API said: no dose, the
 * refusal code in api_error, the detail in api_response_data. Mirrors the retry
 * worker's handling, so a case refused on retry ends in the same shape.
 */
async function recordOutOfScope(
  // The hook's loosened client (dynamic per-arm table names), passed in.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  db: any,
  config: Pick<ArmSubmitConfig<unknown>, 'tables'>,
  caseId: string,
  refusal: OutOfScopeDetail | undefined,
): Promise<void> {
  const code = refusal?.code ?? 'UNKNOWN';
  const { error: apiInsertError } = await db
    .from(config.tables.apiResponses)
    .upsert(
      {
        case_id: caseId,
        api_recommended_dose: null,
        api_error: `OUT_OF_SCOPE: ${code}`,
        api_response_data: { out_of_scope: { ...refusal, status_code: 422 } },
      },
      { onConflict: 'case_id' },
    );
  if (apiInsertError) console.error('⚠️ Failed to record the out-of-scope api_responses row:', apiInsertError);

  const { error: caseUpdateError } = await db
    .from(config.tables.cases)
    .update({ status: 'out_of_scope', last_retry_error: null })
    .eq('id', caseId);

  if (caseUpdateError) {
    console.error('🚨 Failed to record the case as out of scope:', caseUpdateError);
    toast({
      variant: 'destructive',
      title: 'Προσοχή: η υπόθεση ίσως δεν καταγράφηκε σωστά',
      description: `Αποθηκεύτηκε η αξιολόγησή σας, αλλά απέτυχε η καταγραφή της ως εκτός πεδίου. Σημειώστε το ID: ${String(
        caseId,
      ).slice(0, 8)} και ενημερώστε τον διαχειριστή.`,
      duration: 15000,
    });
    return;
  }
  toast({
    title: 'Εκτός πεδίου εφαρμογής του αλγορίθμου',
    description:
      'Ο αλγόριθμος δεν καλύπτει αυτή την περίπτωση. Η αξιολόγησή σας αποθηκεύτηκε ως αποκλεισμένη υπόθεση και δεν θα γίνει νέα προσπάθεια.',
    duration: 9000,
  });
}

export interface SubmitOptions {
  startTime: number;
  /** Clears the local draft + unsaved-changes flag; called on success and on flagged failure. */
  clearDraft: () => void;
}

export function useAssessmentSubmit<TClinical>(config: ArmSubmitConfig<TClinical>) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitPhase, setSubmitPhase] = useState<SubmitPhase>('idle');

  const submit = async (
    clinicalData: TClinical,
    clinicianResponse: ClinicianResponse,
    opts: SubmitOptions,
  ): Promise<void> => {
    if (!user) return;

    setSubmitting(true);
    setSubmitPhase('saving');
    const responseTime = Math.floor((Date.now() - opts.startTime) / 1000);
    // Table names are dynamic per arm, which the generated literal-union client
    // type can't express; scope the loosened client to this hook only.
    const db = supabase as any;

    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();
      if (!profile) throw new Error('User profile not found');

      const { data: clinicalCase, error: caseError } = await db
        .from(config.tables.cases)
        .insert(config.buildCaseInsert(clinicalData))
        .select()
        .single();
      if (caseError) throw caseError;

      const primaryDose = parseDoseCombination(clinicianResponse.recommended_dose).dailyAverage;

      const { data: clinicianResponseData, error: responseError } = await db
        .from(config.tables.clinicianResponses)
        .insert({
          case_id: clinicalCase.id,
          clinician_id: profile.id,
          recommended_dose: primaryDose,
          treatment_recommendation: clinicianResponse.treatment_recommendation,
          notes: clinicianResponse.notes,
          response_time_seconds: responseTime,
        })
        .select()
        .single();
      if (responseError) throw responseError;

      try {
        setSubmitPhase('scoring');
        const railwayData = await callRailwayAPI(config.buildRailwayPayload(clinicalData), {
          url: config.railwayUrl,
          payloadKey: config.payloadKey,
        });
        setSubmitPhase('reconciling');

        const { data: apiResponseData, error: apiError } = await db
          .from(config.tables.apiResponses)
          .insert({
            case_id: clinicalCase.id,
            api_recommended_dose: railwayData.daily_dose,
            api_treatment_recommendation: railwayData.message || 'No treatment recommendation',
            api_response_data: railwayData,
            api_error: null,
            ...(config.buildApiResponseExtra?.(railwayData) ?? {}),
          })
          .select()
          .single();
        if (apiError) throw apiError;

        const tierResult = calculateTierConcordance(
          clinicianResponse.recommended_dose,
          clinicianResponse.treatment_recommendation || '',
          railwayData.daily_dose,
        );
        const secondaryDose = clinicianResponse.treatment_recommendation
          ? parseDoseCombination(clinicianResponse.treatment_recommendation).dailyAverage
          : null;
        const apiDose = railwayData.daily_dose || 0;
        const concordanceResults = {
          dose_concordance_percentage:
            tierResult.tier === 1 ? 100 : tierResult.tier === 2 ? 95 : tierResult.tier === 3 ? 80 : 50,
          treatment_concordance: tierResult.isAcceptable,
          absolute_dose_difference: Math.abs(primaryDose - apiDose),
          tier: tierResult.tier,
          tier_description: tierResult.description,
          clinical_dose_primary: primaryDose,
          clinical_dose_secondary: secondaryDose,
          percentage_difference: apiDose !== 0 ? ((primaryDose - apiDose) / apiDose) * 100 : 0,
        };

        const { error: concordanceError } = await db
          .from(config.tables.concordanceResults)
          .insert({
            case_id: clinicalCase.id,
            clinician_response_id: clinicianResponseData.id,
            api_response_id: apiResponseData.id,
            ...concordanceResults,
          })
          .select();
        if (concordanceError) {
          throw new Error('Failed to save concordance results: ' + JSON.stringify(concordanceError));
        }

        // The one place a case earns 'complete' — only now that the algorithm
        // ran AND concordance was persisted. Rows are born 'pending_retry'.
        const { error: statusError } = await db
          .from(config.tables.cases)
          .update({ status: 'complete' })
          .eq('id', clinicalCase.id);
        if (statusError) console.error('⚠️ status→complete failed:', statusError);

        opts.clearDraft();
        navigate(`/result/${config.arm}/${clinicalCase.id}`);
      } catch (apiError: any) {
        if (apiError?.errorCategory === 'OUT_OF_SCOPE') {
          // The device refused the case at its API perimeter (CR-2026-011). That is
          // its correct answer, not a failed call: record it once as 'out_of_scope'
          // and never retry it. The analysis reads status = 'complete' only, so the
          // case stays outside it by construction.
          await recordOutOfScope(db, config, clinicalCase.id, apiError.outOfScope);
          opts.clearDraft();
          return;
        }

        // Railway call, api_response insert, or concordance insert failed after
        // the clinician response committed. Flag the orphan for the requeue
        // worker instead of leaving a silent split-brain (F-01).
        console.error('❌ Railway/reconcile error:', apiError);

        const errorCategory = apiError?.errorCategory || 'UNKNOWN';
        const errorMessage = apiError?.message || 'Unknown error';
        const errorLabel = `${errorCategory}: ${errorMessage}`;

        // ignoreDuplicates on the UNIQUE(case_id) key: this catch also fires when
        // the Railway call and the success api_response row both landed and only
        // the concordance insert failed. In that case the row already attached to
        // the case is a *good* one, and recording the error must not overwrite it
        // — the requeue worker reconciles from it. Where there is no row yet
        // (the common Railway-failure path) this behaves exactly like the insert
        // it replaces.
        const { error: apiInsertError } = await db
          .from(config.tables.apiResponses)
          .upsert(
            {
              case_id: clinicalCase.id,
              api_error: errorLabel,
              api_response_data: {
                error_details: {
                  message: errorMessage,
                  category: errorCategory,
                  timestamp: new Date().toISOString(),
                },
              },
            },
            { onConflict: 'case_id', ignoreDuplicates: true },
          );
        if (apiInsertError) console.error('⚠️ Failed to record api_responses error row:', apiInsertError);

        const { error: caseUpdateError } = await db
          .from(config.tables.cases)
          .update({
            status: 'pending_retry',
            retry_attempts: 1,
            last_retry_error: errorLabel,
            last_retry_at: new Date().toISOString(),
          })
          .eq('id', clinicalCase.id);

        if (caseUpdateError) {
          console.error('🚨 Failed to flag case for retry:', caseUpdateError);
          toast({
            variant: 'destructive',
            title: 'Προσοχή: η υπόθεση ίσως δεν καταγράφηκε για επανάληψη',
            description: `Αποθηκεύτηκε η αξιολόγησή σας, αλλά υπήρξε σφάλμα στην καταγραφή. Σημειώστε το ID: ${String(
              clinicalCase.id,
            ).slice(0, 8)} και ενημερώστε τον διαχειριστή.`,
            duration: 15000,
          });
        } else {
          toast({
            variant: 'destructive',
            title: 'Σφάλμα Αλγορίθμου',
            description:
              (apiError?.userMessage || 'Αδυναμία σύνδεσης με το σύστημα αλγορίθμου.') +
              ' Η αξιολόγησή σας αποθηκεύτηκε και η σύγκριση θα ξαναδοκιμαστεί αυτόματα. Δείτε το "Σε εκκρεμότητα" στην κεφαλίδα.',
            duration: 9000,
          });
        }

        opts.clearDraft();
      }
    } catch (error) {
      console.error('Error:', error);
      toast({
        variant: 'destructive',
        title: 'Σφάλμα αποθήκευσης',
        description: 'Απέτυχε η αποθήκευση της αξιολόγησης. Παρακαλώ δοκιμάστε ξανά.',
      });
    } finally {
      setSubmitting(false);
      setSubmitPhase('idle');
    }
  };

  return { submit, submitting, submitPhase };
}
